import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronLeft, MapPin, Home, Zap, CalendarDays, Loader2, ArrowRight, Package as PackageIcon } from 'lucide-react';
import { format as formatDate, addDays, parseISO } from 'date-fns';
import { CATEGORY_NAMES, formatCurrency, getTrainerPriceForCategory, waiverOk } from '../lib/utils';
import { getTrainerAvailability, getMyPackagePurchases, bookSessionWithPackage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const TZ_LABELS = {
  'America/New_York': 'Eastern Time',
  'America/Chicago': 'Central Time',
  'America/Denver': 'Mountain Time',
  'America/Phoenix': 'Arizona Time',
  'America/Los_Angeles': 'Pacific Time',
  'America/Anchorage': 'Alaska Time',
  'Pacific/Honolulu': 'Hawaii Time',
};

const CATEGORY_OPTIONS = [
  { value: 'personal_training', label: CATEGORY_NAMES.personal_training },
  { value: 'self_defense', label: CATEGORY_NAMES.self_defense },
  { value: 'jiujitsu', label: CATEGORY_NAMES.jiujitsu },
];

/** Two-mode booking dialog.
 *  bookingType = 'instant' | 'scheduled'
 *  Flow:
 *    1) Service (always)
 *    2) Date+Time (only for 'scheduled')
 *    3) Location (always: client_address or trainer_address)
 *    4) Continue → navigates to /checkout with payload
 */
const BookingDialog = ({ open, onOpenChange, trainer, bookingType }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isClient } = useAuth();
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsTz, setSlotsTz] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [locationType, setLocationType] = useState('client_address');
  const [clientAddress, setClientAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [purchases, setPurchases] = useState([]);
  const [usePackage, setUsePackage] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  const profile = trainer?.trainer_profile || {};
  const trainerAddress = profile.trainer_address || profile.service_area || '';
  const trainerAllowsAtAddress = !!profile.accepts_at_my_location && !!trainerAddress;
  const isInstant = bookingType === 'instant';

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setLiabilityAccepted(false);
    setCategory('');
    setDate(isInstant ? formatDate(new Date(), 'yyyy-MM-dd') : '');
    setTime(isInstant ? (() => {
      const d = new Date();
      const m = d.getMinutes() < 30 ? '30' : '00';
      const h = (d.getMinutes() < 30 ? d.getHours() : (d.getHours() + 1) % 24).toString().padStart(2, '0');
      return `${h}:${m}`;
    })() : '');
    setSlots([]);
    setLocationType(trainerAllowsAtAddress ? 'client_address' : 'client_address');
    setClientAddress('');
    setNotes('');
    setUsePackage(false);
  }, [open, isInstant, trainerAllowsAtAddress]);

  // Load client's active packages once dialog opens
  useEffect(() => {
    if (!open || !isAuthenticated || !isClient) return;
    getMyPackagePurchases()
      .then((res) => setPurchases(res.data || []))
      .catch(() => setPurchases([]));
  }, [open, isAuthenticated, isClient]);

  // Find redeemable purchase for the current trainer + selected category
  const redeemablePurchase = useMemo(() => {
    if (!category || !trainer?.id) return null;
    return purchases.find(
      (p) => p.trainer_id === trainer.id &&
        p.status === 'active' &&
        (p.sessions_remaining ?? 0) > 0 &&
        p.package_snapshot?.category === category
    ) || null;
  }, [purchases, category, trainer?.id]);

  // Auto-default: if a credit is available, opt-in
  useEffect(() => {
    setUsePackage(!!redeemablePurchase);
  }, [redeemablePurchase]);

  // Fetch availability when date changes (scheduled flow only)
  useEffect(() => {
    if (isInstant || !trainer?.id || !date) return;
    setLoadingSlots(true);
    getTrainerAvailability(trainer.id, date)
      .then((res) => { setSlots(res.data?.slots || []); setSlotsTz(res.data?.timezone || ''); })
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [isInstant, trainer?.id, date]);

  const price = useMemo(() => (category ? getTrainerPriceForCategory(profile, category) : 0), [category, profile]);
  const offered = CATEGORY_OPTIONS.filter(
    (o) => Number(getTrainerPriceForCategory(profile, o.value)) > 0
  );

  // Steps:
  //  Instant   → 0: Service, 1: Location, 2: Review
  //  Scheduled → 0: Service, 1: Date/Time, 2: Location, 3: Review
  const [liabilityAccepted, setLiabilityAccepted] = useState(false);

  const steps = isInstant ? ['Service', 'Location', 'Review'] : ['Service', 'When', 'Location', 'Review'];

  const isReviewStep = step === steps.length - 1;

  const canNext = (() => {
    if (step === 0) return !!category && price > 0;
    if (!isInstant && step === 1) return !!date && !!time;
    const locStep = isInstant ? 1 : 2;
    if (step === locStep) {
      if (locationType === 'trainer_address') return trainerAllowsAtAddress;
      return !!clientAddress.trim();
    }
    if (isReviewStep && !liabilityAccepted) return false;
    return true;
  })();

  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const proceedToCheckout = async () => {
    if (!isAuthenticated || !isClient) {
      navigate('/login');
      return;
    }
    if (!waiverOk(user)) {
      // Waiver gate, bounce to existing TrainerProfile booking which will trigger the waiver modal.
      toast.message('You\u2019ll need to accept the liability waiver first.');
      onOpenChange(false);
      return;
    }

    // Package credit path, no Stripe checkout, book directly
    if (usePackage && redeemablePurchase) {
      setRedeeming(true);
      try {
        const res = await bookSessionWithPackage({
          purchase_id: redeemablePurchase.id,
          booking_type: isInstant ? 'instant' : 'scheduled',
          scheduled_date: date,
          scheduled_time: time,
          duration_minutes: 60,
          session_location_type: locationType,
          location_address: locationType === 'trainer_address' ? trainerAddress : clientAddress.trim(),
          notes: notes.trim() || null,
        });
        toast.success(`Package credit redeemed, ${res.data.sessions_remaining} sessions left`);
        onOpenChange(false);
        const sessionId = res.data?.session?.id;
        if (isInstant && sessionId) {
          navigate(`/sessions/${sessionId}/wait`, {
            state: { sessionId, bookingType: 'instant', prepaid: true },
          });
        } else {
          navigate('/sessions', { state: { justBookedPackage: true } });
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Could not redeem package credit';
        toast.error(typeof msg === 'string' ? msg : 'Could not redeem package credit');
      } finally {
        setRedeeming(false);
      }
      return;
    }

    onOpenChange(false);
    navigate('/checkout', {
      state: {
        trainerId: trainer.id,
        trainerName: trainer.full_name,
        trainerImage: profile.profile_image,
        category,
        categoryLabel: CATEGORY_NAMES[category] || category,
        price,
        booking_type: isInstant ? 'instant' : 'scheduled',
        scheduled_date: date,
        scheduled_time: time,
        duration_minutes: 60,
        session_location_type: locationType,
        location_address: locationType === 'trainer_address' ? trainerAddress : clientAddress.trim(),
        notes: notes.trim() || null,
      },
    });
  };

  if (!trainer) return null;

  const locStepIndex = isInstant ? 1 : 2;
  const reviewStepIndex = steps.length - 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d0d10] border-zinc-800 max-w-lg text-white" data-testid="booking-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            {isInstant ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[10px] font-semibold uppercase tracking-wider">
                <Zap className="w-3 h-3" /> Instant
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-200 text-[10px] font-semibold uppercase tracking-wider">
                <CalendarDays className="w-3 h-3" /> Scheduled
              </span>
            )}
            <span className="text-xs text-zinc-500">Step {step + 1} of {steps.length}</span>
          </div>
          <DialogTitle className="text-white text-xl">
            {isInstant ? `Book ${trainer.full_name} now` : `Schedule with ${trainer.full_name}`}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            {steps[step]}
          </DialogDescription>
        </DialogHeader>

        {/* Step content */}
        <div className="py-4 min-h-[200px]">
          {step === 0 && (
            <div className="space-y-3">
              {offered.length === 0 ? (
                <p className="text-sm text-zinc-400" data-testid="no-services">
                  This trainer hasn&apos;t set any prices yet.
                </p>
              ) : (
                offered.map((o) => {
                  const p = getTrainerPriceForCategory(profile, o.value);
                  return (
                    <button
                      type="button"
                      key={o.value}
                      onClick={() => setCategory(o.value)}
                      data-testid={`booking-service-${o.value}`}
                      className={`w-full flex items-center justify-between p-4 rounded-lg border transition-colors ${
                        category === o.value
                          ? 'border-[#C0A062] bg-[#C0A062]/10'
                          : 'border-zinc-800 hover:border-zinc-600'
                      }`}
                    >
                      <span className="font-medium">{o.label}</span>
                      <span className="text-[#C0A062] font-bold">{formatCurrency(p)}</span>
                    </button>
                  );
                })
              )}
            </div>
          )}

          {!isInstant && step === 1 && (
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label className="text-zinc-300 text-xs">Date</Label>
                <Input
                  type="date"
                  value={date}
                  min={formatDate(new Date(), 'yyyy-MM-dd')}
                  max={formatDate(addDays(new Date(), 30), 'yyyy-MM-dd')}
                  onChange={(e) => { setDate(e.target.value); setTime(''); }}
                  className="bg-[#09090B] border-zinc-700 text-white"
                  data-testid="booking-date"
                />
              </div>
              <div>
                <Label className="text-zinc-300 text-xs mb-2 block">Pick a time</Label>
                {loadingSlots ? (
                  <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading slots&hellip;</div>
                ) : !date ? (
                  <p className="text-zinc-500 text-sm">Choose a date first.</p>
                ) : slots.length === 0 ? (
                  <p className="text-zinc-500 text-sm" data-testid="no-slots">
                    No open slots on this day. Try another date.
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-2" data-testid="slots-grid">
                    {slots.map((s) => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => setTime(s)}
                        data-testid={`slot-${s}`}
                        className={`py-2 text-sm rounded-md border transition-colors ${
                          time === s
                            ? 'border-[#C0A062] bg-[#C0A062]/10 text-white'
                            : 'border-zinc-800 text-zinc-300 hover:border-zinc-600'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
                {slotsTz && !!date && slots.length > 0 && (
                  <p className="text-[11px] text-zinc-500 mt-2" data-testid="slots-timezone-note">
                    Times shown in your coach&apos;s local time ({TZ_LABELS[slotsTz] || slotsTz.replace(/_/g, ' ')}).
                  </p>
                )}
              </div>
            </div>
          )}

          {step === locStepIndex && (
            <div className="space-y-3" data-testid="booking-location-step">
              <button
                type="button"
                onClick={() => setLocationType('client_address')}
                className={`w-full flex items-start gap-3 p-4 rounded-lg border text-left transition-colors ${
                  locationType === 'client_address'
                    ? 'border-[#C0A062] bg-[#C0A062]/10'
                    : 'border-zinc-800 hover:border-zinc-600'
                }`}
                data-testid="loc-option-client"
              >
                <Home className="w-5 h-5 text-[#C0A062] mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">Come to me</p>
                  <p className="text-xs text-zinc-400">Trainer travels to your address.</p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => trainerAllowsAtAddress && setLocationType('trainer_address')}
                disabled={!trainerAllowsAtAddress}
                className={`w-full flex items-start gap-3 p-4 rounded-lg border text-left transition-colors ${
                  locationType === 'trainer_address'
                    ? 'border-[#C0A062] bg-[#C0A062]/10'
                    : 'border-zinc-800 hover:border-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
                data-testid="loc-option-trainer"
              >
                <MapPin className="w-5 h-5 text-[#C0A062] mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">Go to trainer</p>
                  <p className="text-xs text-zinc-400">
                    {trainerAllowsAtAddress
                      ? trainerAddress
                      : 'This trainer doesn\u2019t accept clients at their location.'}
                  </p>
                </div>
              </button>

              {locationType === 'client_address' && (
                <div className="grid gap-2 pt-2">
                  <Label className="text-zinc-300 text-xs">Your address</Label>
                  <Input
                    value={clientAddress}
                    onChange={(e) => setClientAddress(e.target.value)}
                    placeholder="Street, city, ZIP"
                    className="bg-[#09090B] border-zinc-700 text-white"
                    data-testid="client-address-input"
                  />
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={"Notes for the trainer (apt #, gate code, equipment to bring\u2026)"}
                    rows={2}
                    className="bg-[#09090B] border-zinc-700 text-white text-sm"
                    data-testid="booking-notes"
                  />
                </div>
              )}
            </div>
          )}

          {step === reviewStepIndex && (
            <div className="space-y-3" data-testid="booking-review">
              <Row label="Service" value={CATEGORY_NAMES[category] || category} />
              {!isInstant && <Row label="When" value={`${date} at ${time}`} />}
              {isInstant && <Row label="Type" value="Instant, trainer has 5 minutes to accept" />}
              <Row
                label="Where"
                value={locationType === 'trainer_address' ? `${trainerAddress} (trainer\u2019s location)` : `${clientAddress} (your location)`}
              />

              {redeemablePurchase && (
                <button
                  type="button"
                  onClick={() => setUsePackage((v) => !v)}
                  className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                    usePackage
                      ? 'border-[#C0A062] bg-[#C0A062]/10'
                      : 'border-zinc-700 hover:border-zinc-500'
                  }`}
                  data-testid="use-package-toggle"
                >
                  <PackageIcon className="w-5 h-5 text-[#C0A062] mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white">
                      Use a package credit
                    </p>
                    <p className="text-xs text-zinc-400">
                      {redeemablePurchase.package_snapshot?.name} , {' '}
                      <span className="text-[#C0A062] font-semibold">
                        {redeemablePurchase.sessions_remaining} of {redeemablePurchase.sessions_total} left
                      </span>
                    </p>
                  </div>
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      usePackage ? 'bg-[#C0A062] text-black' : 'bg-zinc-800 text-zinc-400'
                    }`}
                  >
                    {usePackage ? 'On' : 'Off'}
                  </span>
                </button>
              )}

              <div className="border-t border-zinc-800 pt-3 flex items-center justify-between">
                <span className="text-zinc-300">Total</span>
                {usePackage && redeemablePurchase ? (
                  <span className="text-[#C0A062] text-xl font-bold" data-testid="review-total">
                    1 credit
                  </span>
                ) : (
                  <span className="text-[#C0A062] text-xl font-bold" data-testid="review-total">
                    {formatCurrency(price)}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-zinc-500">
                {usePackage && redeemablePurchase
                  ? 'No charge today, one credit from your package will be redeemed.'
                  : 'We\u2019ll authorize your card now. You\u2019re only charged when the trainer accepts. Cancel 24+ hours before the session for a full refund.'}
              </p>

              <label className="flex items-start gap-3 pt-3 cursor-pointer" data-testid="liability-label">
                <input
                  type="checkbox"
                  checked={liabilityAccepted}
                  onChange={(e) => setLiabilityAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-zinc-700 bg-[#09090B] text-[#C0A062] focus:ring-[#C0A062] cursor-pointer accent-[#C0A062] shrink-0"
                  data-testid="liability-checkbox"
                />
                <span className="text-xs text-zinc-300 leading-relaxed">
                  I understand that combat sports and physical training carry inherent risks,
                  and I accept responsibility for my participation in this session.
                </span>
              </label>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-2">
          <Button
            variant="ghost"
            onClick={step === 0 ? () => onOpenChange(false) : back}
            className="text-zinc-400 hover:text-white"
            data-testid="booking-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {step === 0 ? 'Cancel' : 'Back'}
          </Button>
          {step < steps.length - 1 ? (
            <Button
              disabled={!canNext}
              onClick={next}
              className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold"
              data-testid="booking-next-btn"
            >
              Next <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              disabled={!canNext || redeeming}
              onClick={proceedToCheckout}
              className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold"
              data-testid="booking-continue-btn"
            >
              {redeeming && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {usePackage && redeemablePurchase ? 'Redeem credit & send request' : 'Continue to payment'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 text-sm">
    <span className="text-zinc-500">{label}</span>
    <span className="text-white text-right break-words max-w-[60%]">{value}</span>
  </div>
);

export default BookingDialog;
